import { Route } from 'react-router-dom';
import {
  configApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { UnifiedThemeProvider } from '@backstage/theme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { lightTheme, darkTheme } from './themes';
import './styles.css';

import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { mermaidAddonProps } from './techdocsMermaid';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { announcementCreatePermission } from '@backstage-community/plugin-announcements-common';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';
import { HomePage } from './components/home/HomePage';
import { AwsAccountsPage } from './components/aws-accounts/AwsAccountsPage';
import { OrgAccountsAdminPage } from './components/admin/OrgAccountsAdminPage';
import { adminOrgAccountsReadPermission } from '@internal/plugin-admin-common';
import { ScoreBoardPage } from '@internal/plugin-score-card';
import { BackstageAdoptionPage } from '@internal/plugin-backstage-adoption';
import { TdpSkillsTelemetryPage } from '@internal/plugin-tdp-skills-telemetry';
import { TdpStatisticPage } from '@internal/plugin-tdp-statistic';
import { ImcSyncPage } from '@internal/plugin-imc-sync';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  themes: [
    {
      id: 'light',
      title: 'Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={lightTheme} children={children} />
      ),
    },
    {
      id: 'dark',
      title: 'Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={darkTheme} children={children} />
      ),
    },
  ],
  components: {
    // Entra ID is the only sign-in provider (ADR-0021, feature 0036). `guest`
    // is local dev only; every other authMode value — including the now-vestigial
    // 'microsoft-only' — resolves to the same single Microsoft button.
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      const authMode = configApi.getOptionalString('app.authMode');

      if (authMode === 'guest') {
        return <SignInPage {...props} auto providers={['guest']} />;
      }

      return (
        <SignInPage
          {...props}
          providers={[
            {
              id: 'microsoft-auth-provider',
              title: 'Microsoft',
              message: 'Sign in using Microsoft Entra ID',
              apiRef: microsoftAuthApiRef,
            },
          ]}
        />
      );
    },
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        <Mermaid {...mermaidAddonProps} />
      </TechDocsAddons>
    </Route>
    <Route
      path="/score-board"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <ScoreBoardPage />
        </RequirePermission>
      }
    />
    <Route
      path="/backstage-adoption"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <BackstageAdoptionPage />
        </RequirePermission>
      }
    />
    <Route
      path="/announcements"
      element={
        <RequirePermission permission={announcementCreatePermission}>
          <AnnouncementsPage />
        </RequirePermission>
      }
    />

    <Route
      path="/create"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <ScaffolderPage />
        </RequirePermission>
      }
    />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>

    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/aws-accounts" element={<AwsAccountsPage />} />
    <Route
      path="/admin/org-accounts"
      element={
        <RequirePermission permission={adminOrgAccountsReadPermission}>
          <OrgAccountsAdminPage />
        </RequirePermission>
      }
    />
    <Route path="/tdp-skills-telemetry" element={<TdpSkillsTelemetryPage />} />
    <Route path="/tdp-statistic" element={<TdpStatisticPage />} />
    <Route path="/imc-sync" element={<ImcSyncPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
